import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Grid,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Icon,
  Select,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FiArrowLeft, FiFileText } from 'react-icons/fi';
import FichaPreviewPanel from 'views/Dashboard/Profile/components/Fichas/FichaPreviewPanel';
import { groupRawNames } from 'components/PedagogicalExit/ExitTargetsFields';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const Chip = ({ children, colorBg, colorInk }) => (
  <Box as="span" px="10px" py="4px" borderRadius="999px" bg={colorBg} color={colorInk} fontSize="11.5px" fontWeight="700">
    {children}
  </Box>
);

const DetailField = ({ label, children }) => (
  <Box>
    <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".04em" mb="4px">
      {label}
    </Text>
    {children}
  </Box>
);

/**
 * Vista de detalle de una salida pedagógica — mismo patrón que
 * ExitSummaryView.js del Scanner (detalle a la izquierda, ficha a la
 * derecha vía FichaPreviewPanel), pero con el detalle en 2 columnas
 * internas, igual que el formulario de creación (ExitTargetsFields a la
 * izquierda / fechas+responsable+autoriza a la derecha). Reemplaza el
 * antiguo botón "Ver ficha" + ModalSurveyResponse — ahora siempre se puede
 * abrir el detalle, tenga o no fichas firmadas todavía.
 */
const PedagogicalExitDetailModal = ({ isOpen, onClose, exit, courseOptions }) => {
  const [selectedResponseId, setSelectedResponseId] = useState(null);
  const modalSize = useBreakpointValue({ base: 'full', md: '6xl' });

  useEffect(() => {
    if (!isOpen) return;
    setSelectedResponseId(exit?.survey_responses?.[0]?.id ?? null);
  }, [isOpen, exit]);

  if (!exit) return null;

  const courses = groupRawNames(exit.group_names, courseOptions);
  const students = exit.student_ids ?? [];
  const responses = exit.survey_responses ?? [];
  const selectedResponse = responses.find((r) => r.id === selectedResponseId) ?? responses[0] ?? null;

  const fichaItem = selectedResponse ? {
    id: selectedResponse.id,
    created_at: selectedResponse.created_at,
    survey: { name: 'Salida Pedagógica — Responsable' },
    pastel: 'lav',
  } : null;

  const fichaCustomer = {
    first_name: exit.title,
    last_name: '',
    username: exit.authorized_by ? `Autorizado por ${exit.authorized_by}` : '',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalSize} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx={3} bg="cream.bg" minH={{ md: '700px' }}>
        <ModalHeader pr="48px">{exit.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Detalle (2fr, con sus propias 2 columnas internas) + ficha
              firmada (1fr) — en mobile todo se apila. */}
          <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap="18px" alignItems="start">
            <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px" p="24px">
              <HStack spacing="12px" mb="20px">
                <Flex w="54px" h="54px" borderRadius="14px" bg="lav.bg" align="center" justify="center" flexShrink={0}>
                  <Icon as={FiArrowLeft} boxSize="26px" color="lav.ink" />
                </Flex>
                <Box minW={0}>
                  <Text fontSize="13.5px" fontWeight="800" color="lav.ink" letterSpacing=".04em">
                    SALIDA PEDAGÓGICA
                  </Text>
                  <HStack spacing="6px" mt="2px">
                    <Box
                      as="span" px="8px" py="2px" borderRadius="999px"
                      bg={exit.active ? '#DCF1E6' : 'cream.bg2'}
                      color={exit.active ? '#1E7A4E' : 'ink.400'}
                      fontSize="11px" fontWeight="700"
                    >
                      {exit.active ? 'Activo' : 'Inactivo'}
                    </Box>
                    <Text fontSize="12.5px" color="ink.400" fontWeight="600">
                      {formatDate(exit.starts_at)} → {formatDate(exit.ends_at)}
                    </Text>
                  </HStack>
                </Box>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <VStack align="stretch" spacing={5}>
                  <DetailField label="Cursos / grupos">
                    {courses.length > 0 ? (
                      <Flex gap="6px" flexWrap="wrap">
                        {courses.map((c) => (
                          <Chip key={c.code} colorBg="lav.bg" colorInk="lav.ink">{c.label}</Chip>
                        ))}
                      </Flex>
                    ) : (
                      <Text fontSize="13px" color="ink.400">Sin cursos asignados</Text>
                    )}
                  </DetailField>

                  <DetailField label="Alumnos individuales">
                    {students.length > 0 ? (
                      <Flex gap="6px" flexWrap="wrap">
                        {students.map((s) => (
                          <Chip key={s.id} colorBg="purple.50" colorInk="purple.700">
                            {s.name}{s.username ? ` · ${s.username}` : ''}
                          </Chip>
                        ))}
                      </Flex>
                    ) : (
                      <Text fontSize="13px" color="ink.400">Sin alumnos individuales</Text>
                    )}
                  </DetailField>
                </VStack>

                <VStack align="stretch" spacing={5}>
                  <HStack spacing={6} align="flex-start">
                    <DetailField label="Desde">
                      <Text fontSize="13.5px" color="ink.900" fontWeight="600">{formatDate(exit.starts_at)}</Text>
                    </DetailField>
                    <DetailField label="Hasta">
                      <Text fontSize="13.5px" color="ink.900" fontWeight="600">{formatDate(exit.ends_at)}</Text>
                    </DetailField>
                  </HStack>

                  <DetailField label="Responsable">
                    <Text fontSize="13.5px" color="ink.900" fontWeight="600">
                      {exit.responsible_name || '—'}
                    </Text>
                  </DetailField>

                  <DetailField label="Autorizado por">
                    <Text fontSize="13.5px" color="ink.900" fontWeight="600">
                      {exit.authorized_by || '—'}
                    </Text>
                  </DetailField>
                </VStack>
              </SimpleGrid>
            </Box>

            <Box>
              {responses.length > 1 && (
                <Select
                  size="sm" mb={3} bg="white" borderRadius="8px"
                  value={selectedResponseId ?? ''}
                  onChange={(e) => setSelectedResponseId(Number(e.target.value))}
                >
                  {responses.map((r) => (
                    <option key={r.id} value={r.id}>
                      Ficha #{r.id} — {formatDate(r.created_at)}
                    </option>
                  ))}
                </Select>
              )}

              {fichaItem ? (
                <FichaPreviewPanel item={fichaItem} customer={fichaCustomer} />
              ) : (
                <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px" p="40px 24px" textAlign="center">
                  <Icon as={FiFileText} boxSize={8} color="ink.400" mb={3} />
                  <Text fontSize="13.5px" color="ink.700" fontWeight="700">Sin fichas firmadas</Text>
                  <Text fontSize="12.5px" color="ink.400" mt={1}>
                    Todavía nadie ha registrado su salida con esta ficha.
                  </Text>
                </Box>
              )}
            </Box>
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PedagogicalExitDetailModal;
